// One formatter for quantities, percentages and durations (rev-7, D-426).
//
// The audit found the same three faults on a dozen screens: quantities printed
// at the wire's scale ("400.000 m", "Min 1.950000 · Target 2.000000 mm"),
// yields printed as fractions ("0.3894"), a total with no unit
// ("GOOD 61,999.024"), and durations as "0 min 0 s". These helpers fix each
// one in the reader's locale and are pure, so a screen can adopt them without
// a hook.
//
// A quantity on the wire is an exact decimal string (D-041). It goes to
// `Intl.NumberFormat` as a string, which formats it exactly rather than
// through a float, so "61999.024" never reads as 61,999.023999.
//
// A unit CODE is pack data (`ohm_per_km`, `pcs`, `mm2`) and never reaches the
// glass: `unitSymbol` prints the symbol the reader's language writes for it
// ("Ω/km", "шт.", "mm²") from the core `unit` namespace (design-system §3.9). A code the
// namespace does not know is a unit the tenant named itself and is shown as
// typed, except that a snake_case code is made readable and logged, because
// that shape is a developer's and not a plant's.

import i18next from "i18next";

/**
 * The dimensions whose units count whole things (pieces, drums, coils). The
 * fact comes from the unit's own record (`Uom.dimension`, spec 01 §2.1), never
 * from a list of unit codes: codes are pack data, and a pack is free to name a
 * drum anything (doctrine rule 5).
 */
const WHOLE_DIMENSIONS: ReadonlySet<string> = new Set(["count", "container"]);

/** The most fraction digits any quantity shows unless the caller says more. */
export const DEFAULT_QTY_PRECISION = 3;

/** The longest fraction the formatter will ever print. */
const MAX_FRACTION_DIGITS = 6;

/** The fields of a `Uom` record the formatter reads. */
export interface QuantityUom {
  readonly code: string;
  readonly dimension?: string | null | undefined;
  readonly decimal_precision?: number | null | undefined;
}

export interface QuantityOptions {
  /**
   * The unit's record. Supplies the code printed after the number, the
   * dimension and the display precision; `unit`, `dimension` and `precision`
   * override it field by field.
   */
  uom?: QuantityUom | null | undefined;
  /** The unit printed after the number, always. Omit only for a bare count. */
  unit?: string | null | undefined;
  /**
   * The unit's dimension (`Uom.dimension`). A count or container unit prints
   * no padding zeros, and a fraction it does hold is shown rather than rounded
   * to a whole number.
   */
  dimension?: string | null | undefined;
  /** Most fraction digits to show. Trailing zeros are always trimmed. */
  precision?: number | null | undefined;
  /** BCP 47 locale; defaults to the runtime's. */
  locale?: string | undefined;
  /** Print a leading + for a positive value, as a change does. */
  signed?: boolean | undefined;
}

function clampDigits(digits: number): number {
  return Math.max(0, Math.min(MAX_FRACTION_DIGITS, Math.trunc(digits)));
}

/** How many fraction digits the value itself carries, trailing zeros trimmed. */
function ownFractionDigits(value: string | number): number {
  const text = typeof value === "number" ? String(value) : value.trim();
  if (/e/iu.test(text)) return MAX_FRACTION_DIGITS;
  const fraction = text.split(".")[1]?.replace(/0+$/u, "") ?? "";
  return fraction.length;
}

function fractionDigitsFor(value: string | number, options: QuantityOptions): number {
  const dimension = options.dimension ?? options.uom?.dimension;
  const whole = dimension !== null && dimension !== undefined && WHOLE_DIMENSIONS.has(dimension);
  const precision = options.precision ?? options.uom?.decimal_precision;
  // A whole-unit quantity normally reads "12 pcs". When it really holds a
  // fraction ("12.5" pieces, "0.4" of a drum) the fraction is the fact, and
  // rounding it to "13 pcs" or "0 drums" would print a number nobody recorded.
  if (whole) return clampDigits(Math.max(precision ?? 0, ownFractionDigits(value)));
  return clampDigits(precision ?? DEFAULT_QTY_PRECISION);
}

/** True for a finite number or a decimal string the formatter can read. */
function readable(value: string | number): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  return /^\s*[-+]?\d+(?:\.\d+)?\s*$/u.test(value);
}

/** True when the value is not zero but rounds to zero at `digits` places. */
function roundsToZero(value: string | number, digits: number): boolean {
  const n = Math.abs(typeof value === "number" ? value : Number(value));
  return n > 0 && n < 0.5 * 10 ** -digits;
}

/**
 * "400 m", "61,999.024 kg", "12 pcs". Trailing zeros are trimmed to the unit's
 * precision and the unit is always printed. A value that is not zero never
 * prints as zero: one too small for the precision shows its first two
 * significant digits ("0.0004 kg"). An absent or unreadable value is the empty
 * string, so the caller decides what an unknown looks like (a dash, a
 * `RefLabel`) rather than reading a fake zero.
 */
export function formatQuantity(
  value: string | number | null | undefined,
  options: QuantityOptions = {},
): string {
  if (value === null || value === undefined || !readable(value)) return "";
  const digits = fractionDigitsFor(value, options);
  const signDisplay = options.signed === true ? "exceptZero" : "auto";
  const nf = roundsToZero(value, digits)
    ? new Intl.NumberFormat(options.locale, { maximumSignificantDigits: 2, signDisplay })
    : new Intl.NumberFormat(options.locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
        signDisplay,
      });
  // A decimal string is formatted exactly (Intl.NumberFormat v3); the cast is
  // for TypeScript lib versions that still type `format` as number-only.
  const text = nf.format((typeof value === "string" ? value.trim() : value) as number);
  const code = (options.unit ?? options.uom?.code)?.trim();
  if (code === undefined || code === "") return text;
  const count = typeof value === "number" ? value : Number(value);
  return `${text}\u00a0${unitSymbol(code, { locale: options.locale, count })}`;
}

export interface UnitSymbolOptions {
  /** BCP 47 locale; defaults to the reader's language. */
  locale?: string | undefined;
  /** The quantity, so a counted unit agrees with it ("1 bobbin", "3 bobbins"). */
  count?: number | undefined;
}

/** A code the `unit` namespace may hold: letters, digits and underscores. */
const UNIT_KEY = /^[a-z][a-z0-9_]*$/iu;

/** Codes already reported as unknown, so a long list logs each one once. */
const reportedUnits = new Set<string>();

/**
 * The symbol of a unit in the reader's language: "m", "м", "公尺"; "Ω/km";
 * "3 bobbins". Percent is the sign in every language. A unit the tenant
 * named itself is printed as named.
 */
export function unitSymbol(code: string, options: UnitSymbolOptions = {}): string {
  const trimmed = code.trim();
  if (trimmed === "pct") return "%";
  if (!UNIT_KEY.test(trimmed)) return trimmed;
  if (i18next.isInitialized) {
    const missing = "\u0000";
    const count = options.count;
    const label = i18next.t(`unit.${trimmed}`, {
      lng: options.locale ?? i18next.language,
      defaultValue: missing,
      ...(count !== undefined && Number.isFinite(count) ? { count } : {}),
    });
    if (typeof label === "string" && label !== missing && label.length > 0) return label;
  }
  if (!trimmed.includes("_")) return trimmed;
  if (!reportedUnits.has(trimmed)) {
    reportedUnits.add(trimmed);
    console.warn(`unitSymbol: no symbol for unit code "${trimmed}"; add it to the unit namespace`);
  }
  return trimmed.replace(/_per_/gu, "/").replace(/_/gu, " ");
}

export interface PercentOptions {
  /** Most fraction digits of the percentage. */
  precision?: number | undefined;
  locale?: string | undefined;
}

/**
 * A ratio as a percentage: 0.3894 → "38.9%" ("38,9 %" in Russian). A yield,
 * a scrap rate or an off-list rate always reads this way, never as a fraction.
 */
export function formatRatioAsPercent(
  ratio: string | number | null | undefined,
  options: PercentOptions = {},
): string {
  if (ratio === null || ratio === undefined || !readable(ratio)) return "";
  const n = typeof ratio === "number" ? ratio : Number(ratio);
  return new Intl.NumberFormat(options.locale, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: options.precision ?? 1,
  }).format(n);
}

/** A value already in percent: "0.200000" → "0.2%". */
export function formatPercent(
  percent: string | number | null | undefined,
  options: PercentOptions = {},
): string {
  if (percent === null || percent === undefined || !readable(percent)) return "";
  const n = typeof percent === "number" ? percent : Number(percent);
  return formatRatioAsPercent(n / 100, options);
}

type DurationUnit = "day" | "hour" | "minute" | "second";

const UNIT_SECONDS: readonly (readonly [DurationUnit, number])[] = [
  ["day", 86_400],
  ["hour", 3_600],
  ["minute", 60],
  ["second", 1],
];

export interface DurationOptions {
  locale?: string | undefined;
  /** How many units to show at most, largest first. Default 2. */
  parts?: number | undefined;
}

/**
 * A duration in seconds as its largest units, skipping zeros: 3725 → "1 hr,
 * 2 min" ("1 ч 2 мин" in Russian). Under a second reads "0 sec" once, never
 * "0 min 0 s". Unit names come from the locale's own data, so no i18n key is
 * needed.
 */
export function formatDuration(
  seconds: number | null | undefined,
  options: DurationOptions = {},
): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "";
  const negative = seconds < 0;
  let rest = Math.round(Math.abs(seconds));
  const limit = Math.max(1, options.parts ?? 2);
  const out: string[] = [];
  // Units are counted from the largest non-zero one, so 3601 s reads "1 hr"
  // rather than skipping the empty minutes to print "1 hr 1 sec".
  let considered = 0;
  for (const [unit, size] of UNIT_SECONDS) {
    if (considered >= limit) break;
    const count = Math.floor(rest / size);
    rest -= count * size;
    if (count > 0 || out.length > 0) considered += 1;
    if (count > 0) {
      out.push(
        new Intl.NumberFormat(options.locale, {
          style: "unit",
          unit,
          unitDisplay: "short",
        }).format(count),
      );
    }
  }
  if (out.length === 0) {
    out.push(
      new Intl.NumberFormat(options.locale, {
        style: "unit",
        unit: "second",
        unitDisplay: "short",
      }).format(0),
    );
  }
  const text = out.join(" ");
  return negative ? `−${text}` : text;
}
